import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../ui/AppIcon';
import { isIOSSafari } from '../../utils/pwaPlatform';

const SHEET_Z_INDEX = 2147483000;

/**
 * iPhone / iPad install UI — Apple does not allow one-tap PWA install.
 * This sheet walks the client through Add to Home Screen in Safari.
 */
export default function IOSInstallSheet({ open, onClose }) {
  const inSafari = typeof window !== 'undefined' ? isIOSSafari() : true;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('pwa-install-guide-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('pwa-install-guide-open');
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const steps = inSafari
    ? [
        {
          title: 'Tap Share',
          body: 'At the bottom of Safari, tap the Share button (square with an arrow up).',
          icon: 'ArrowUpOnSquareIcon',
        },
        {
          title: 'Add to Home Screen',
          body: 'Scroll the share sheet and tap “Add to Home Screen”.',
          icon: 'PlusCircleIcon',
        },
        {
          title: 'Tap Add',
          body: 'Confirm the name “Patel Sales”, then tap Add. The app icon appears on your Home Screen.',
          icon: 'CheckCircleIcon',
        },
      ]
    : [
        {
          title: 'Open in Safari',
          body: 'This browser cannot install the app. Tap ⋯ or Share → “Open in Safari”.',
          icon: 'GlobeAltIcon',
        },
        {
          title: 'Tap Share in Safari',
          body: 'In Safari, tap Share (square with arrow up) at the bottom.',
          icon: 'ArrowUpOnSquareIcon',
        },
        {
          title: 'Add to Home Screen',
          body: 'Tap “Add to Home Screen”, then Add. Open Patel Sales from your Home Screen next time.',
          icon: 'CheckCircleIcon',
        },
      ];

  const sheet = (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ zIndex: SHEET_Z_INDEX }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ios-install-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/65 border-0 cursor-pointer"
        aria-label="Close"
        onClick={onClose}
      />

      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ zIndex: 1 }}
      >
        {/* iOS-style grab handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        <div className="px-5 pt-2 pb-6 sm:p-6">
          <div className="flex items-start gap-3 mb-5">
            <img
              src="/icons/icon-180x180.png"
              alt=""
              className="w-14 h-14 rounded-[14px] shadow-md shrink-0"
            />
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id="ios-install-title" className="text-xl font-bold text-gray-900">
                Install on iPhone
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {inSafari
                  ? 'Add Patel Sales to your Home Screen'
                  : 'Please continue in Safari to install'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 -mr-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label="Close"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {!inSafari && (
            <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
              <p className="text-xs text-amber-900 leading-relaxed font-medium">
                Chrome, Instagram, or Facebook in-app browser cannot add apps. Use Safari.
              </p>
            </div>
          )}

          <ol className="space-y-4 mb-6">
            {steps.map((step, index) => (
              <li key={step.title} className="flex gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#003087] text-white text-sm font-bold">
                    {index + 1}
                  </span>
                  {index < steps.length - 1 && (
                    <span className="w-0.5 flex-1 min-h-[12px] bg-gray-200 mt-1" />
                  )}
                </div>
                <div className="pb-1 pt-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={step.icon} size={18} className="text-[#003087] shrink-0" />
                    <p className="font-bold text-gray-900 text-sm">{step.title}</p>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="rounded-xl bg-[#003087]/5 border border-[#003087]/15 px-3 py-3 mb-5">
            <p className="text-xs text-[#003087] leading-relaxed">
              After install, open <strong>Patel Sales</strong> from your Home Screen — it runs like
              an app (full screen, fast ordering).
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-[52px] rounded-xl bg-[#003087] hover:bg-[#00266d] text-white font-bold text-base transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
